import { Badge, Button } from '@/shared/ui';
import { Link } from '@tanstack/react-router';
import {
  HISTORY_FILTER_OPTIONS,
  usePaymentSettingsHistoryView,
} from '../hooks/use-payment-settings-history-view.hook';
import { SettingsChangesTable } from './settings-changes-table.component';
import { SettingsSection } from './settings-section.component';

type PaymentSettingsHistoryTabProps = {
  productCode: string;
};

/** Aba "Histórico de alterações": quem mudou o quê e quando, com o "de/para". */
export function PaymentSettingsHistoryTab({
  productCode,
}: PaymentSettingsHistoryTabProps) {
  const {
    entries,
    filter,
    setFilter,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
  } = usePaymentSettingsHistoryView(productCode);

  return (
    <SettingsSection
      title='Histórico de alterações'
      description={
        <>
          Cada alteração da cobrança ou do catálogo fica registrada com o autor
          e o &quot;de/para&quot;. A mesma trilha aparece em{' '}
          <Link
            to='/settings/audit'
            className='font-medium text-alba-blue-500 underline'
          >
            Configurações › Auditoria
          </Link>
          .
        </>
      }
    >
      <div
        role='group'
        aria-label='Filtrar o histórico'
        className='flex flex-wrap gap-2'
      >
        {HISTORY_FILTER_OPTIONS.map(option => (
          <Button
            key={option.value}
            type='button'
            size='sm'
            variant={filter === option.value ? 'primary' : 'outline'}
            aria-pressed={filter === option.value}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <p className='text-sm text-alba-gray-500'>Carregando o histórico...</p>
      )}
      {error && <p className='text-sm text-alba-error-red-600'>{error}</p>}
      {!isLoading && !error && entries.length === 0 && (
        <p className='rounded-lg bg-alba-gray-200/50 p-4 text-center text-sm text-alba-gray-600'>
          Nenhuma alteração registrada{hasNextPage ? ' nesta página' : ''}.
        </p>
      )}

      <ol className='flex flex-col gap-3'>
        {entries.map(entry => (
          <li
            key={entry.id}
            className='flex flex-col gap-3 rounded-lg border border-alba-gray-200 p-3'
          >
            <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
              <div className='min-w-0'>
                <p className='text-sm text-alba-black-500'>
                  <span className='font-semibold'>{entry.actor}</span> ·{' '}
                  {entry.actionLabel}
                </p>
                <p className='text-xs text-alba-gray-500'>
                  {[entry.actorEmail, entry.when].filter(Boolean).join(' · ')}
                </p>
              </div>
              <Badge tone={entry.kind === 'gateway' ? 'purple' : 'blue'}>
                {entry.kind === 'gateway' ? 'Gateway' : 'Catálogo'}
              </Badge>
            </div>
            {entry.description && (
              <p className='text-sm text-alba-gray-600'>{entry.description}</p>
            )}
            {entry.changes.length > 0 && (
              <SettingsChangesTable
                changes={entry.changes}
                hiddenChanges={entry.hiddenChanges}
              />
            )}
          </li>
        ))}
      </ol>

      {hasNextPage && (
        <div className='flex justify-center'>
          <Button
            type='button'
            variant='outline'
            size='md'
            onClick={loadMore}
            loading={isFetchingNextPage}
          >
            Carregar mais
          </Button>
        </div>
      )}
    </SettingsSection>
  );
}
